import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { CalendarDays, CheckSquare, Phone, StickyNote, type LucideIcon } from 'lucide-react';
import { Button } from '@elka/ui';
import type { CallConnection } from '@elka/types';
import { useAuth } from '../../auth/hooks/useAuth';
import { useTicketActions } from '../../tickets/useTicketActions';
import { useCrmActions } from '../../shell/useCrmActions';
import { FIELD_TEXTAREA } from '../../board/pageStyles';
import { useLogActivity } from '../hooks/useLogActivity';
import {
  FOLLOW_UP_OPTIONS,
  OUTCOME_OPTIONS,
  buildLogInput,
  daysFromTodayISO,
  followUpOption,
  loggedMessage,
} from '../utils/quickLog';

const CHIP_ON = 'border-brand bg-brand-soft text-brand';
const CHIP_OFF = 'border-line bg-surface text-muted hover:bg-surface-soft hover:text-ink-2';

/** ענה / לא ענה / השארתי הודעה / ביקש לחזור. */
export function OutcomeChips({
  value,
  onChange,
}: {
  value: CallConnection;
  onChange: (value: CallConnection) => void;
}) {
  return (
    <div role="group" aria-label="תוצאת השיחה" className="flex flex-wrap gap-1.5">
      {OUTCOME_OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={`rounded-chip border px-2.5 py-1 text-xs font-bold transition-colors ${
            value === o.value ? CHIP_ON : CHIP_OFF
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** "משימת המשך: ללא · מחר · בעוד 3 ימים · בעוד שבוע"; children sit at the row's end. */
export function FollowUpChips({
  value,
  onChange,
  label = 'משימת המשך:',
  includeNone = true,
  children,
}: {
  /** Days from today; null = none. */
  value: number | null;
  onChange: (days: number | null) => void;
  label?: string;
  /** false drops the "ללא" chip (a task always has a due date). */
  includeNone?: boolean;
  children?: ReactNode;
}) {
  const options = includeNone
    ? FOLLOW_UP_OPTIONS
    : FOLLOW_UP_OPTIONS.filter((o) => o.days !== null);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-muted text-xs font-semibold">{label}</span>
      {options.map((o) => (
        <button
          key={o.label}
          type="button"
          aria-pressed={value === o.days}
          onClick={() => onChange(o.days)}
          className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors ${
            value === o.days ? CHIP_ON : CHIP_OFF
          }`}
        >
          {o.label}
        </button>
      ))}
      {children}
    </div>
  );
}

type ComposerTab = 'call' | 'meeting' | 'note' | 'task';

const TABS: { key: ComposerTab; label: string; icon: LucideIcon; placeholder: string }[] = [
  { key: 'call', label: 'שיחה', icon: Phone, placeholder: 'מה עלה בשיחה?' },
  { key: 'meeting', label: 'פגישה', icon: CalendarDays, placeholder: 'סיכום הפגישה...' },
  { key: 'note', label: 'הערה', icon: StickyNote, placeholder: 'הערה פנימית...' },
  { key: 'task', label: 'משימה', icon: CheckSquare, placeholder: 'מה צריך לעשות?' },
];

export interface ComposerProps {
  dealId?: string;
  dealName?: string;
  companyId: string;
  companyName: string;
  contactId?: string;
  contactName?: string;
}

/**
 * Inline log card on the deal and customer pages: call / meeting / note through
 * useLogActivity (optional follow-up task), or a task for me in the tasks app. Replaces
 * the quick-log, activity and task modals there.
 */
export function Composer(target: ComposerProps) {
  const { user } = useAuth();
  const { toast } = useCrmActions();
  const { logActivity } = useLogActivity();
  const { createTicket } = useTicketActions();
  const [tab, setTab] = useState<ComposerTab>('call');
  const [outcome, setOutcome] = useState<CallConnection>('answered');
  const [text, setText] = useState('');
  const [followDays, setFollowDays] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isTask = tab === 'task';
  const needsText = tab === 'note' || isTask;
  const canSave = !!user && !isSaving && (!needsText || !!text.trim());

  const save = async () => {
    if (!user || !canSave) return;
    const performer = { uid: user.uid, name: user.displayName || user.email || '' };
    setIsSaving(true);
    try {
      if (isTask) {
        await createTicket({
          title: text.trim(),
          dueDate: daysFromTodayISO(followDays ?? 1),
          priority: 'normal',
          assigneeId: performer.uid,
          assigneeName: performer.name,
          dealId: target.dealId,
          dealName: target.dealName,
          customer: target.companyName,
        });
        toast('המשימה נוספה');
      } else {
        await logActivity(
          buildLogInput(tab, target, text, performer, outcome),
          followUpOption(followDays, performer)
        );
        toast(loggedMessage(tab, followDays !== null));
      }
      setText('');
      setFollowDays(null);
      setOutcome('answered');
    } catch (err) {
      console.error('Composer save failed:', err);
      toast('השמירה נכשלה, נסה שוב', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // RTL: the next tab is to the left.
  const handleTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowLeft: 1, ArrowRight: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const index = TABS.findIndex((t) => t.key === tab);
    const next = TABS[(index + step + TABS.length) % TABS.length];
    setTab(next.key);
    event.currentTarget.querySelector<HTMLElement>(`[data-tab="${next.key}"]`)?.focus();
  };

  const placeholder = TABS.find((t) => t.key === tab)?.placeholder;

  return (
    <div className="border-line bg-surface rounded-xl border">
      <div
        role="tablist"
        aria-label="רישום"
        onKeyDown={handleTabKeyDown}
        className="border-line flex gap-0.5 border-b px-1.5 pt-1.5"
      >
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            data-tab={key}
            aria-selected={tab === key}
            tabIndex={tab === key ? 0 : -1}
            onClick={() => setTab(key)}
            className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] transition-colors ${
              tab === key
                ? 'border-brand text-ink font-bold'
                : 'text-muted hover:text-ink-2 border-transparent font-medium'
            }`}
          >
            <Icon size={15} aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>

      <div role="tabpanel" className="flex flex-col gap-2.5 p-3.5">
        {tab === 'call' && <OutcomeChips value={outcome} onChange={setOutcome} />}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void save();
            }
          }}
          rows={3}
          placeholder={placeholder}
          aria-label={placeholder}
          className={`${FIELD_TEXTAREA} resize-y`}
        />
        <FollowUpChips
          value={isTask ? (followDays ?? 1) : followDays}
          onChange={setFollowDays}
          label={isTask ? 'תאריך יעד:' : undefined}
          includeNone={!isTask}
        >
          <Button
            calm
            variant="primary"
            onClick={save}
            disabled={!canSave}
            isLoading={isSaving}
            className="ms-auto"
          >
            {isTask ? 'הוסף משימה' : 'שמור'}
          </Button>
        </FollowUpChips>
      </div>
    </div>
  );
}
